"use client";

import { useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

const applicants = [
  {
    icon: "BuildingOffice2Icon",
    title: "County Governments",
    description: "Submit county-owned parks, cultural sites, and tourism circuits for national profiling.",
    examples: ["Narok County — Maasai Mara", "Kilifi County — Malindi Marine Park"],
    color: "red",
  },
  {
    icon: "HomeModernIcon",
    title: "Tourism Enterprises",
    description: "Hotels, lodges, camps, tour operators, and travel agencies seeking national exposure.",
    examples: ["Eco-lodges & Safari Camps", "Boutique Hotels & Resorts"],
    color: "green",
  },
  {
    icon: "UserGroupIcon",
    title: "Community Groups",
    description: "Community-based tourism organizations offering authentic local experiences.",
    examples: ["Samburu Cultural Villages", "Rusinga Island Fishing Tours"],
    color: "red",
  },
  {
    icon: "SparklesIcon",
    title: "Cultural Organizers",
    description: "Festivals, cultural events, and heritage experiences with tourism potential.",
    examples: ["Lamu Cultural Festival", "Maralal Camel Derby"],
    color: "green",
  },
  {
    icon: "BanknotesIcon",
    title: "Investors",
    description: "Investment-ready tourism opportunities seeking Ministry endorsement and partnership.",
    examples: ["New Resort Developments", "Tourism Infrastructure Projects"],
    color: "red",
  },
  {
    icon: "CalendarDaysIcon",
    title: "Event Organizers",
    description: "Sports, adventure, and entertainment events that attract domestic and international tourists.",
    examples: ["Safari Rally Kenya", "Rhino Charge Adventure Race"],
    color: "green",
  },
];

export default function WhoCanApplySection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll(".who-item");
            items.forEach((item, i) => {
              setTimeout(() => item.classList.add("revealed"), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-warm-bg relative overflow-hidden">
      {/* Subtle pattern */}
      <div className="absolute inset-0 opacity-[0.025]">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="triangles" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
              <polygon points="30,5 55,50 5,50" fill="#1A1A1A" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#triangles)" />
        </svg>
      </div>

      <div className="max-w-8xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-20 who-item reveal-hidden">
          <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-kenya-green/8 border border-kenya-green/20 mb-6">
            <div className="w-2 h-2 rounded-full bg-kenya-green" />
            <span className="text-[0.6875rem] font-bold uppercase tracking-[0.4em] text-kenya-green font-condensed">
              Who Can Apply
            </span>
          </div>
          <h2
            className="font-display font-bold text-kenya-black mb-4 sm:mb-5"
            style={{ fontSize: "clamp(2rem, 5vw, 4.5rem)", lineHeight: "0.92", letterSpacing: "-0.025em" }}
          >
            Every Voice in
            <br />
            <span className="font-display italic font-light text-kenya-red">Kenyan Tourism</span>
          </h2>
          <p className="text-text-muted max-w-xl mx-auto text-base sm:text-lg leading-relaxed">
            Whether you manage a national park or lead a community village tour — if you offer a unique Kenyan experience, this portal is for you.
          </p>
        </div>

        {/* Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {applicants.map((item, idx) => (
            <div
              key={item.title}
              className={`who-item reveal-hidden group p-6 sm:p-8 rounded-xl2 border-2 transition-all duration-500 cursor-default bg-white ${
                item.color === "red" ?"border-kenya-red/12 hover:border-kenya-red/35 hover:shadow-card-red" :"border-kenya-green/12 hover:border-kenya-green/35 hover:shadow-card-green"
              }`}
              style={{ transitionDelay: `${idx * 80}ms` }}
            >
              <div
                className={`w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center mb-4 sm:mb-6 ${
                  item.color === "red" ? "bg-kenya-red/10" : "bg-kenya-green/10"
                } group-hover:scale-110 transition-transform`}
              >
                <Icon
                  name={item.icon as Parameters<typeof Icon>[0]["name"]}
                  size={24}
                  className={item.color === "red" ? "text-kenya-red" : "text-kenya-green"}
                />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-kenya-black mb-2 sm:mb-3 font-display">{item.title}</h3>
              <p className="text-text-muted text-[0.875rem] sm:text-[0.9375rem] leading-relaxed mb-4 sm:mb-5">{item.description}</p>
              <div className="space-y-2.5">
                {item.examples.map((ex) => (
                  <div key={ex} className="flex items-center gap-2.5">
                    <div
                      className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        item.color === "red" ? "bg-kenya-red/50" : "bg-kenya-green/50"
                      }`}
                    />
                    <span className="text-[0.8125rem] text-text-muted font-medium">{ex}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}